/**
 * Toy Registry - 动态加载和管理 Toy 组件
 */

import { trackToyLoad } from './performance'
import { logError, getFriendlyErrorMessage } from './error-handler'

// 使用 Vite 的 import.meta.glob 动态导入所有 Toy 组件
const toyModules = import.meta.glob('../toys/**/index.vue')
const toyConfigs = import.meta.glob('../toys/**/config.{json,js}')

function resolvePaths(toyId) {
  const directComponent = `../toys/${toyId}/index.vue`
  const directConfigJson = `../toys/${toyId}/config.json`
  const directConfigJs = `../toys/${toyId}/config.js`
  const directConfig = toyConfigs[directConfigJs] ? directConfigJs : directConfigJson
  if (toyModules[directComponent] && toyConfigs[directConfig]) {
    return { componentPath: directComponent, configPath: directConfig }
  }
  const componentFallback = Object.keys(toyModules).find(p => p.endsWith(`/${toyId}/index.vue`))
  const configFallback = Object.keys(toyConfigs).find(p => p.endsWith(`/${toyId}/config.json`) || p.endsWith(`/${toyId}/config.js`))
  if (componentFallback && configFallback) {
    return { componentPath: componentFallback, configPath: configFallback }
  }
  return null
}

/**
 * 加载指定 Toy 的组件和配置
 * @param {string} toyId - Toy 的唯一标识符 (例如: 'mathematics/linear-regression')
 * @returns {Promise<{component: Object, config: Object}>}
 */
export async function loadToy(toyId) {
  // Start performance tracking
  const tracker = trackToyLoad(toyId)
  
  try {
    const resolved = resolvePaths(toyId)
    const componentPath = resolved?.componentPath || `../toys/${toyId}/index.vue`
    const configPath = resolved?.configPath || `../toys/${toyId}/config.json`

    const [componentModule, configModule] = await Promise.all([
      toyModules[componentPath]?.(),
      toyConfigs[configPath]?.()
    ])

    if (!componentModule || !configModule) {
      throw new Error(`Toy not found: ${toyId}`)
    }

    // End performance tracking
    const duration = tracker.end()
    
    // Log warning if load time is too slow
    if (duration > 1000) {
      console.warn(`⚠️ Slow Toy load detected: ${toyId} took ${duration.toFixed(2)}ms`)
    }

    return {
      component: componentModule.default,
      config: configModule.default
    }
  } catch (error) {
    tracker.end() // End tracking even on error
    logError('ToyRegistry.loadToy', error, { toyId })
    
    return {
      error: true,
      message: getFriendlyErrorMessage(error) || '抱歉，该 Toy 暂时无法加载',
      originalError: error
    }
  }
}

/**
 * 生成所有 Toy 的元数据索引
 * 优化：使用并行加载，显著提升加载速度（从 10-20s 降至 1-2s）
 * @returns {Promise<Array>}
 */
export async function generateToysIndex() {
  // Skip JSON when a JS config exists for the same toy
  const jsBases = new Set(
    Object.keys(toyConfigs)
      .filter(p => /config\.js$/.test(p))
      .map(p => p.replace(/config\.js$/, ''))
  )

  // 过滤有效的配置路径
  const validPaths = Object.keys(toyConfigs).filter(path => {
    if (/config\.json$/.test(path)) {
      const base = path.replace(/config\.json$/, '')
      if (jsBases.has(base)) return false
    }
    return true
  })

  // 并行加载所有配置（关键优化：从顺序加载改为并行加载）
  const loadPromises = validPaths.map(async (path) => {
    try {
      const configModule = await toyConfigs[path]()
      const config = configModule.default

      // 从路径中提取 toyId
      const match = path.match(/\.\.\/toys\/(.+)\/config\.(json|js)/)
      if (match) {
        config.path = match[1]
        if (!config.id) {
          const segs = match[1].split('/')
          config.id = segs[segs.length - 1]
        }
        // 排除未发布的 toy（published: false 或 hidden: true）
        // 如果未设置 published 字段，默认为 true（向后兼容）
        const isPublished = config.published !== false && config.hidden !== true
        if (isPublished) {
          return config
        }
      }
      return null
    } catch (error) {
      console.error(`Failed to load config from ${path}`, error)
      return null
    }
  })

  const results = await Promise.all(loadPromises)
  return results.filter(Boolean)
}

/**
 * 获取所有可用的 Toy ID 列表
 * @returns {Array<string>}
 */
export function getAvailableToyIds() {
  return Object.keys(toyModules).map(path => {
    const match = path.match(/\.\.\/toys\/(.+)\/index\.vue/)
    return match ? match[1] : null
  }).filter(Boolean)
}
